import "../../assets/styles/keycloack/patternfly.min.css";
import "../../assets/styles/keycloack/patternfly(1).min.css";
import "../../assets/styles/keycloack/patternfly-additions.min.css";
import "../../assets/styles/keycloack/pficon.css";
import "../../assets/styles/keycloack/stylelogin.css";
import { FaEyeSlash } from "react-icons/fa";
import { IoEyeSharp } from "react-icons/io5";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { getAccessToken } from "~/services/keycloak";
import { SCRIPTS_PATH } from "~/services/endpoints";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";

interface FormErrors {
  username?: string;
  password?: string;
}

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAuthError(null);

    const newErrors: FormErrors = {};
    const rawUsername = usernameRef.current?.value.trim() || "";
    const rawPassword = passwordRef.current?.value.trim() || "";

    if (!rawUsername) newErrors.username = "Username is required";
    if (!rawPassword) newErrors.password = "Password is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await getAccessToken(rawUsername, rawPassword);
      if (response.access_token) {
        sessionStorage.setItem("id_token", response.id_token);
        sessionStorage.setItem("access_token", response.access_token);
        sessionStorage.setItem("refresh_token", response.refresh_token);
        navigate(SCRIPTS_PATH, { replace: true });
      } else {
        setAuthError("Authentication failed. Please check your credentials.");
      }
    } catch (err: any) {
      setAuthError("Authentication failed. Please check your credentials.");
    } finally {
      setIsSubmitting(false);
      if (passwordRef.current) passwordRef.current.value = "";
      if (usernameRef.current) usernameRef.current.value = "";
    }
  };

  return (
    <div className="login-pf">
      <div className="login-pf-page">
        <div id="kc-header" className="login-pf-page-header">
          <div id="kc-header-wrapper">participant</div>
        </div>

        <div className="card-pf">
          <header className="login-pf-header">
            <h1 id="kc-page-title">Sign in to your account</h1>
          </header>

          <div id="kc-content">
            <div id="kc-content-wrapper">
              <div id="kc-form">
                <div id="kc-form-wrapper">
                  <form id="kc-form-login" onSubmit={onFormSubmit}>
                    {/* Username */}
                    <div className="form-group">
                      <label
                        htmlFor="username"
                        className="pf-c-form__label pf-c-form__label-text"
                      >
                        Username or email
                      </label>
                      <div
                        className="pf-c-input-group"
                        style={{ position: "relative" }}
                      >
                        <input
                          id="username"
                          className={`pf-c-form-control ${authError ? "pf-m-error" : ""}`}
                          name="username"
                          type="text"
                          autoFocus
                          ref={usernameRef}
                        />
                        {authError && (
                          <AiOutlineExclamationCircle
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#c9190b",
                              fontSize: "20px",
                            }}
                          />
                        )}
                      </div>
                      {errors.username && (
                        <span className="pf-c-form__helper-text pf-m-error">
                          {errors.username}
                        </span>
                      )}
                    </div>

                    {/* Password */}
                    <div className="form-group">
                      <label
                        htmlFor="password"
                        className="pf-c-form__label pf-c-form__label-text"
                      >
                        Password
                      </label>
                      <div
                        className="pf-c-input-group"
                        style={{ position: "relative" }}
                      >
                        <input
                          id="password"
                          className={`pf-c-form-control ${authError ? "pf-m-error" : ""}`}
                          name="password"
                          type={showPassword ? "text" : "password"}
                          ref={passwordRef}
                          autoComplete="current-password"
                          aria-describedby={authError ? "password-error" : undefined}
                        />
                        <button
                          className="pf-c-button pf-m-control"
                          type="button"
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          aria-controls="password"
                          onClick={togglePassword}
                        >
                          {showPassword ? (
                            <FaEyeSlash className="fa" />
                          ) : (
                            <IoEyeSharp className="fa" />
                          )}
                        </button>
                        {authError && (
                          <AiOutlineExclamationCircle
                            style={{
                              position: "absolute",
                              right: "40px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#c9190b",
                              fontSize: "20px",
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Global Auth Error */}
                    {authError && (
                      <div
                        className="pf-c-form__helper-text pf-m-error"
                        role="alert"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "#c9190b",
                          marginTop: "1rem",
                        }}
                      >
                        <AiOutlineExclamationCircle
                          style={{ fontSize: "1.2rem", marginRight: "0.5rem" }}
                        />
                        {authError}
                      </div>
                    )}

                    {/* Buttons */}
                    <div id="kc-form-buttons" className="form-group">
                      <input
                        type="hidden"
                        id="id-hidden-input"
                        name="credentialId"
                      />
                      <input
                        className="pf-c-button pf-m-primary pf-m-block btn-lg"
                        name="login"
                        id="kc-login"
                        type="submit"
                        value={isSubmitting ? "Signing In..." : "Sign In"}
                        disabled={isSubmitting}
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;